import { create } from "zustand";

export const useNotificationsStore = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  cursor: null,
  
  pendingOperations: new Set(),

  fetchUnreadCount: async ({ fetchData, appProperties, userId }) => {
    if (!userId) return;
    
    const operationKey = `fetchUnreadCount-${userId}`;
    const { pendingOperations } = get();
    if (pendingOperations.has(operationKey)) {
      return; // Already in progress
    }
    
    set((state) => ({
      pendingOperations: new Set([...state.pendingOperations, operationKey])
    }));

  
    try {
      const response = await fetchData(`notifications/unread-count?osyncUserId=${userId}`, "GET", null, appProperties);
      const resp = typeof response === "string" ? JSON.parse(response) : response;
      const count = Number(resp?.data?.data) || 0;
      console.log("fetchUnreadCount :::::: count", count);
      set({ unreadCount: count });
    } catch (error) {
      console.error("Error fetching unread count:", error);
      set({ unreadCount: 0 });
    } finally {
      set((state) => {
        const newOps = new Set(state.pendingOperations);
        newOps.delete(operationKey);
        return { pendingOperations: newOps };
      });
    }
  },

  fetchNotifications: async ({ fetchDataByPagination, appProperties, userId, reset = false }) => {
    if (!userId) return;

    const { loading, cursor, notifications } = get();
    if (loading) return;

    if (reset) {
      set({ notifications: [], cursor: null });
    }

    set({ loading: true });
    try {
      const currentCursor = reset ? null : cursor;

      const response = await fetchDataByPagination(
        `notifications?osyncUserId=${userId}`,
        "GET",
        null,
        appProperties,
        null,
        20,
        currentCursor,
        null,
        null
      );

      const notificationsData = response?.data;
      if (!Array.isArray(notificationsData)) {
        set({ cursor: null, notifications: reset ? [] : notifications });
        return;
      }

      let nextCursor = null;
      if (response?.paginationModel) {
        if (response.paginationModel.cursor) {
          nextCursor = { cursor: response.paginationModel.cursor };
        } else if (response.paginationModel.lastKey) {
          nextCursor = {
            lastKey: response.paginationModel.lastKey,
            lastRange: response.paginationModel.lastRange || null,
          };
        }
      }

      set((state) => ({
        notifications: reset ? notificationsData : [...state.notifications, ...notificationsData],
        cursor: nextCursor
      }));
    } catch (err) {
      console.error("Error fetching notifications:", err);
      if (reset) set({ notifications: [] });
    } finally {
      set({ loading: false });
    }
  },

  onIncomingNotification: (notification, { addToList = false, incrementUnreadCount = true } = {}) => {
    if (!notification) return;
    
    const notificationId = notification?.notificationId;
    if (!notificationId) return;
    
    set((state) => {
      const existingNotifications = state.notifications || [];
      
      // Check if notification already exists
      const existingIndex = existingNotifications.findIndex(n => n.notificationId === notificationId);
      const notificationExists = existingIndex !== -1;
      
      let updatedNotifications;
      if (addToList) {
        if (notificationExists) {
          // Update existing notification in place
          updatedNotifications = [...existingNotifications];
          updatedNotifications[existingIndex] = notification;
        } else {
          // Add new notification at the beginning
          updatedNotifications = [notification, ...existingNotifications];
        }
      } else {

        if (notificationExists) {
          updatedNotifications = [...existingNotifications];
          updatedNotifications[existingIndex] = notification;
        } else {
          updatedNotifications = existingNotifications;
        }
      }
      
      return {
        unreadCount: incrementUnreadCount ? Math.max(0, (state.unreadCount || 0) + 1) : state.unreadCount,
        notifications: updatedNotifications,
      };
    });
  },

  markAsRead: async ({ fetchData, appProperties, notification }) => {
    if (!notification?.notificationId) return;
    
    const userId = appProperties?.currentUser?.osyncUserId;
    if (!userId) return;
    
    const notificationId = notification.notificationId;
    
    // Prevent concurrent operations on the same notification
    const operationKey = `markAsRead-${notificationId}`;
    const { pendingOperations } = get();
    if (pendingOperations.has(operationKey)) {
      return; // Already in progress
    }
    
    const recipient = notification.recipients?.find(r => r.osyncUserId === userId);
    if (recipient?.status === 1) {
      return; 
    }
    
    set((state) => {
      const updatedNotifications = (state.notifications || []).map((n) => {
        if (n.notificationId === notificationId) {
          const updatedRecipients = (n.recipients || []).map(r => 
            r.osyncUserId === userId ? { ...r, status: 1 } : r
          );
          return { ...n, recipients: updatedRecipients };
        }
        return n;
      });
      
      return {
        notifications: updatedNotifications,
        unreadCount: Math.max(0, (state.unreadCount || 0) - 1),
        pendingOperations: new Set([...state.pendingOperations, operationKey])
      };
    });
    
    try {
      await fetchData(`notifications/${notificationId}/read`, "POST", null, appProperties);
    } catch (error) {
      console.error("Error marking notification as read:", error);
      set((state) => ({
        notifications: (state.notifications || []).map((n) => {
          if (n.notificationId === notificationId) {
            const updatedRecipients = (n.recipients || []).map(r => 
              r.osyncUserId === userId ? { ...r, status: 0 } : r
            );
            return { ...n, recipients: updatedRecipients };
          }
          return n;
        }),
        unreadCount: Math.max(0, (state.unreadCount || 0) + 1),
      }));
    } finally {
      set((state) => {
        const newOps = new Set(state.pendingOperations);
        newOps.delete(operationKey);
        return { pendingOperations: newOps };
      });
    }
  },


  markNotificationsAsReadByIds: ({ notificationIds, userId }) => {
    if (!Array.isArray(notificationIds) || notificationIds.length === 0 || !userId) return;

    set((state) => {
      const currentNotifications = state.notifications || [];
      const newNotificationIdSet = new Set(notificationIds.filter(Boolean));
      let updatedCount = 0;
      const updatedNotifications = currentNotifications.map((n) => {
        if (!newNotificationIdSet.has(n.notificationId)) return n;
        const recipient = n.recipients?.find((r) => r.osyncUserId === userId);
        if (recipient?.status === 1) return n; 
        updatedCount += 1;
        const updatedRecipients = (n.recipients || []).map((r) =>
          r.osyncUserId === userId ? { ...r, status: 1 } : r
        );
        return { ...n, recipients: updatedRecipients };
      });
      return {
        notifications: updatedNotifications,
        unreadCount: Math.max(0, (state.unreadCount || 0) - updatedCount),
      };
    });
  },

  markAllAsRead: async ({ fetchData, appProperties, userId }) => {
    if (!userId) return;
    
    const operationKey = `markAllAsRead-${userId}`;
    const { pendingOperations } = get();
    if (pendingOperations.has(operationKey)) {
      return;
    }
    
    const originalState = {
      notifications: get().notifications,
      unreadCount: get().unreadCount,
    };
    
    set((state) => {
      const updatedNotifications = (state.notifications || []).map((n) => {
        const updatedRecipients = (n.recipients || []).map(r => 
          r.osyncUserId === userId ? { ...r, status: 1 } : r
        );
        return { ...n, recipients: updatedRecipients };
      });
      
      return {
        notifications: updatedNotifications,
        unreadCount: 0,
        pendingOperations: new Set([...state.pendingOperations, operationKey])
      };
    });
    
    try {
      await fetchData(`notifications/read-all?osyncUserId=${userId}`, "POST", null, appProperties);
    } catch (error) {
      console.error("Error marking all as read:", error);
      set({
        notifications: originalState.notifications,
        unreadCount: originalState.unreadCount,
      });
    } finally {
      set((state) => {
        const newOps = new Set(state.pendingOperations);
        newOps.delete(operationKey);
        return { pendingOperations: newOps };
      });
    }
  },

  clearStore: () => {
    set({
      notifications: [],
      unreadCount: 0,
      loading: false,
      cursor: null,
      pendingContactInfo: null,
      pendingOperations: new Set(),
    });
  },
}));